import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Divider,
  IconButton,
  Tooltip,
  Button,
  Avatar
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EmployeeDetailsModal from '../EmployeeDetailsModal/EmployeeDetailsModalEnhanced';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import PhoneIcon from '@material-ui/icons/Phone';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import GroupIcon from '@material-ui/icons/Group';
import AssignmentIcon from '@material-ui/icons/Assignment';
import WarningIcon from '@material-ui/icons/Warning';
import MessageIcon from '@material-ui/icons/Message';
import MapIcon from '@material-ui/icons/Map';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import ScheduleIcon from '@material-ui/icons/Schedule';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import TimerOffIcon from '@material-ui/icons/TimerOff';
import WorkOutlineIcon from '@material-ui/icons/WorkOutline';

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[8],
      transform: 'translateY(-2px)'
    }
  },
  cardHeader: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: 'relative'
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  headerLeft: {
    flex: 1,
    marginRight: theme.spacing(2)
  },
  crewName: {
    fontWeight: 600,
    color: theme.palette.primary.dark,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5)
  },
  entityLogo: {
    margin: '5px',
    maxWidth: 170,
    maxHeight: 40,
    objectFit: 'contain'
  },
  statusChip: {
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    height: 24,
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1)
    }
  },
  activeStatus: {
    backgroundColor: '#4caf50',
    color: '#fff'
  },
  delayedStatus: {
    backgroundColor: '#ff9800',
    color: '#fff'
  },
  idleStatus: {
    backgroundColor: '#f44336',
    color: '#fff'
  },
  breakStatus: {
    backgroundColor: '#2196f3',
    color: '#fff'
  },
  section: {
    marginBottom: theme.spacing(2)
  },
  sectionTitle: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  locationInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  locationChip: {
    fontSize: '0.75rem',
    height: 24,
    '& .MuiChip-icon': {
      fontSize: '1rem'
    }
  },
  employeeList: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  employeeChip: {
    height: 28,
    fontSize: '0.75rem'
  },
  currentJob: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  jobTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(0.5)
  },
  workorderProgress: {
    marginTop: theme.spacing(1)
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5)
  },
  progressText: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  metricBox: {
    textAlign: 'center',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`
  },
  metricValue: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  metricLabel: {
    fontSize: '0.65rem',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary
  },
  alertSection: {
    backgroundColor: '#fff3e0',
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  alertIcon: {
    color: '#ff9800'
  },
  actions: {
    padding: theme.spacing(1.5),
    justifyContent: 'space-between',
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: 'auto'
  },
  actionButton: {
    fontSize: '0.75rem'
  },
  greenText: {
    color: '#4caf50'
  },
  redText: {
    color: '#f44336'
  },
  avatarGroup: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: theme.spacing(0.5),
    '& .MuiAvatar-root': {
      border: `2px solid ${theme.palette.background.paper}`,
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      '&:hover': {
        transform: 'scale(1.1)',
        zIndex: 1000
      }
    }
  },
  avatarMore: {
    backgroundColor: theme.palette.grey[400],
    fontSize: '0.75rem'
  },
  timeAnalysisSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  timeBar: {
    height: 24,
    borderRadius: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    position: 'relative',
    backgroundColor: theme.palette.grey[200],
    overflow: 'hidden'
  },
  timeBarSegment: {
    position: 'absolute',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '0.75rem',
    fontWeight: 500
  },
  timeStats: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      fontSize: '0.75rem'
    }
  }
}));

const CrewCard = ({ crewData }) => {
  const classes = useStyles();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEmployee(null);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'active': return classes.activeStatus;
      case 'delayed': return classes.delayedStatus;
      case 'idle': return classes.idleStatus;
      case 'break': return classes.breakStatus;
      default: return '';
    }
  };

  const getLastUpdateText = (lastUpdate) => {
    const mins = Math.floor((Date.now() - lastUpdate) / 60000);
    
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min ago`;
    
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    
    const weeks = Math.floor(days / 7);
    if (weeks === 1) return '1 week ago';
    if (weeks < 4) return `${weeks} weeks ago`;
    
    const months = Math.floor(days / 30);
    if (months === 1) return '1 month ago';
    if (months < 12) return `${months} months ago`;
    
    // If it's been over a year, something's probably wrong
    return 'No recent update';
  };

  const completionPercent = Math.round(
    ((crewData.todayMetrics?.workorders?.completed || 0) / 
     (crewData.todayMetrics?.workorders?.assigned || 1)) * 100
  );

  return (
    <Card className={classes.card}>
      {/* Header */}
      <Box className={classes.cardHeader}>
        <Box className={classes.headerContent}>
          <Box className={classes.headerLeft}>
            <Typography variant="h6" className={classes.crewName}>
              <GroupIcon />
              {crewData.name}
            </Typography>
            <Chip
              label={crewData.status}
              size="small"
              className={getStatusClass(crewData.status)}
              style={{ marginTop: 4 }}
            />
          </Box>
          {crewData.entity && (
            <img 
              alt="Entity Logo" 
              src={ `${process.env.PUBLIC_URL}/${crewData.entity}_Logo.png`}
              className={classes.entityLogo}
            />
          )}
        </Box>
      </Box>

      <CardContent style={{ flex: 1 }}>
        {/* Employees Section */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            <GroupIcon fontSize="small" />
            Crew Members
          </Typography>
          <Box className={classes.employeeList}>
            <Box className={classes.avatarGroup}>
              {(() => {
                // Sort employees: foreman first, then clocked-in, then not clocked-in
                const sortedEmployees = [...crewData.employees].sort((a, b) => {
                  if (a.role === 'foreman') return -1;
                  if (b.role === 'foreman') return 1;
                  if (a.clockStatus?.isClockedIn && !b.clockStatus?.isClockedIn) return -1;
                  if (!a.clockStatus?.isClockedIn && b.clockStatus?.isClockedIn) return 1;
                  return 0;
                });
                
                const maxDisplay = 4; // Show up to 4 avatars
                const displayEmployees = sortedEmployees.slice(0, maxDisplay);
                const remainingCount = sortedEmployees.length - maxDisplay;
                
                return (
                  <>
                    {displayEmployees.map((emp, index) => (
                      <Tooltip 
                        key={emp.id} 
                        title={`${emp.name}${emp.role === 'foreman' ? ' (Foreman)' : emp.role === 'driver' ? ' (Driver)' : ''}${emp.clockStatus?.isClockedIn ? '' : ' - Not clocked in'}`}
                      >
                        <Avatar 
                          alt={emp.name}
                          src={emp.profilePhotoUrl || undefined}
                          onClick={() => handleEmployeeClick(emp)}
                          style={{ 
                            zIndex: displayEmployees.length - index + (emp.role === 'foreman' ? 10 : 0),
                            width: emp.role === 'foreman' ? 36 : 32,
                            height: emp.role === 'foreman' ? 36 : 32,
                            fontSize: emp.role === 'foreman' ? '1rem' : '0.875rem',
                            opacity: emp.clockStatus?.isClockedIn ? 1 : 0.5,
                            marginLeft: index === 0 ? 0 : -8
                          }}
                        >
                          {!emp.profilePhotoUrl && (emp.name || 'E').charAt(0).toUpperCase()}
                        </Avatar>
                      </Tooltip>
                    ))}
                    {remainingCount > 0 && (
                      <Avatar 
                        className={classes.avatarMore} 
                        style={{ 
                          zIndex: 0,
                          marginLeft: -8
                        }}
                      >
                        +{remainingCount}
                      </Avatar>
                    )}
                  </>
                );
              })()}
            </Box>
            <Typography variant="caption" color="textSecondary">
              {crewData.employees.filter(e => e.id !== 'no_employees').length} members
              {crewData.employees.some(e => e.clockStatus?.isClockedIn) && 
                ` • ${crewData.employees.filter(e => e.clockStatus?.isClockedIn).length} clocked in`}
            </Typography>
          </Box>
        </Box>

        {/* Location Section */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            <LocationOnIcon fontSize="small" />
            Location Tracking
          </Typography>
          <Box className={classes.locationInfo}>
            <Chip
              icon={<PhoneIcon />}
              label={getLastUpdateText(crewData.location?.phone?.lastUpdate || new Date())}
              size="small"
              className={classes.locationChip}
              color={crewData.location?.gpsStatus === 'suspicious' ? 'secondary' : 'primary'}
            />
            <Chip
              icon={<DirectionsCarIcon />}
              label={`GPS: ${crewData.location?.gpsStatus || 'unknown'}`}
              size="small"
              className={classes.locationChip}
              color={
                crewData.location?.gpsStatus === 'matched' ? 'primary' :
                crewData.location?.gpsStatus === 'suspicious' ? 'secondary' :
                'default'
              }
            />
          </Box>
          <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
            📍 {crewData.location?.locationName || 'Unknown location'}
          </Typography>
        </Box>

        {/* Current Job */}
        {crewData.currentJob?.name && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              <AssignmentIcon fontSize="small" />
              Current Activity
            </Typography>
            <Box className={classes.currentJob}>
              <Typography variant="body2" className={classes.jobTitle}>
                {crewData.currentJob.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                <ScheduleIcon style={{ fontSize: 14, verticalAlign: 'middle' }} />
                {' '}Scheduled: {crewData.currentJob.scheduledTime}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Workorder Progress */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            <TrendingUpIcon fontSize="small" />
            Today's Progress
          </Typography>
          <Box className={classes.workorderProgress}>
            <LinearProgress
              variant="determinate"
              value={completionPercent}
              className={classes.progressBar}
              color={completionPercent > 70 ? "primary" : "secondary"}
            />
            <Box className={classes.progressText}>
              <span>{crewData.todayMetrics?.workorders?.completed || 0} completed</span>
              <span>{crewData.todayMetrics?.workorders?.assigned || 0} total</span>
            </Box>
          </Box>
        </Box>

        {/* Performance Metrics */}
        <Box className={classes.metricsGrid}>
          <Box className={classes.metricBox}>
            <Typography className={classes.metricValue}>
              {crewData.todayMetrics?.performance?.efficiency || 0}%
            </Typography>
            <Typography className={classes.metricLabel}>
              Efficiency
            </Typography>
          </Box>
          <Box className={classes.metricBox}>
            <Typography 
              className={classes.metricValue}
              style={{ 
                color: (crewData.vehicle?.metrics?.fuelLevel || 0) < 25 ? '#f44336' :
                       (crewData.vehicle?.metrics?.fuelLevel || 0) < 50 ? '#ff9800' :
                       '#4caf50'
              }}
            >
              <LocalGasStationIcon style={{ fontSize: 16, verticalAlign: 'middle' }} />
              {' '}{crewData.vehicle?.metrics?.fuelLevel || 0}%
            </Typography>
            <Typography className={classes.metricLabel}>
              Fuel Level
            </Typography>
          </Box>
          <Box className={classes.metricBox}>
            <Typography 
              className={classes.metricValue}
              style={{ 
                color: (crewData.vehicle?.metrics?.idleTime?.offsite || 0) > 15 ? '#ff9800' : 'inherit'
              }}
            >
              <TimerOffIcon style={{ fontSize: 16, verticalAlign: 'middle' }} />
              {' '}{crewData.vehicle?.metrics?.idleTime?.offsite || 0}m
            </Typography>
            <Typography className={classes.metricLabel}>
              Idle Off-site
            </Typography>
          </Box>
        </Box>

        {/* Time Analysis - Only show if we have time data */}
        {crewData.todayMetrics?.timeAnalysis && crewData.todayMetrics.timeAnalysis.totalClockedHours > 0 && (
          <Box className={classes.timeAnalysisSection}>
            <Typography className={classes.sectionTitle}>
              <AccessTimeIcon fontSize="small" />
              Time Utilization
            </Typography>
            
            {/* Visual time bar */}
            <Box className={classes.timeBar}>
              {/* Working time segment */}
              <Box 
                className={classes.timeBarSegment}
                style={{
                  left: 0,
                  width: `${crewData.todayMetrics.timeAnalysis.utilizationRate || 0}%`,
                  backgroundColor: '#4caf50'
                }}
              >
                {crewData.todayMetrics.timeAnalysis.utilizationRate > 15 && 
                  `${crewData.todayMetrics.timeAnalysis.utilizationRate}%`
                }
              </Box>
            </Box>
            
            {/* Time statistics */}
            <Box className={classes.timeStats}>
              <div>
                <WorkOutlineIcon style={{ fontSize: 14, color: '#4caf50' }} />
                <span>{crewData.todayMetrics.timeAnalysis.totalWorkingHours}h working</span>
              </div>
              <div>
                <AccessTimeIcon style={{ fontSize: 14, color: '#666' }} />
                <span>{crewData.todayMetrics.timeAnalysis.totalClockedHours}h clocked</span>
              </div>
            </Box>
          </Box>
        )}

        {/* Alerts */}
        {crewData.alerts.length > 0 && (
          <Box className={classes.alertSection}>
            <WarningIcon className={classes.alertIcon} fontSize="small" />
            <Typography variant="caption">
              {crewData.alerts[0]}
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Actions */}
      <CardActions className={classes.actions}>
        <Box>
          <Tooltip title="Send Message">
            <IconButton size="small" color="primary">
              <MessageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Map">
            <IconButton size="small" color="primary">
              <MapIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          className={classes.actionButton}
        >
          View Details
        </Button>
      </CardActions>
      
      {/* Employee Details Modal */}
      <EmployeeDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        employee={selectedEmployee}
      />
    </Card>
  );
};

export default CrewCard;